import { ChevronLeft, ChevronRight, LoaderCircle, Paperclip, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { getPqrCategoryTranslationKey } from "@/features/pqr/pqrMappers";
import { StatusBadge } from "@/features/pqr/components/StatusBadge";
import { type PqrHistoryItem } from "@/features/pqr/pqr.types";
import { cn } from "@/lib/utils";

type MyRequestsListProps = {
  activeRequestId: string | null;
  isLoading?: boolean;
  items: PqrHistoryItem[];
  onOpenRequest: (requestId: string) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  page: number;
  pageCount: number;
  searchQuery: string;
  totalCount: number;
};

export function MyRequestsList({
  activeRequestId,
  isLoading = false,
  items,
  onOpenRequest,
  onPageChange,
  onSearchChange,
  page,
  pageCount,
  searchQuery,
  totalCount,
}: MyRequestsListProps) {
  const { i18n, t } = useTranslation();
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium",
  });

  return (
    <aside className="flex min-h-full flex-col border-r border-[#ece2d6]">
      <div className="border-b border-[#ece2d6] px-6 py-6">
        <label className="flex items-center gap-3 border-b border-[#e2d7c8] pb-4 text-ink/52">
          <Search className="size-5 shrink-0" />
          <input
            className="w-full bg-transparent text-base text-ink outline-none placeholder:text-ink/38"
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t("pqr.myRequests.searchPlaceholder", { defaultValue: "Search requests..." })}
            type="search"
            value={searchQuery}
          />
        </label>
      </div>

      <div className="border-b border-[#ece2d6] px-6 py-5">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-ink/52">
          {t("pqr.myRequests.listTitle")}
        </p>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-sm text-ink/58">
            {t("pqr.myRequests.pageSummary", { page, pageCount, totalCount })}
          </p>
          {isLoading ? (
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-ink/45">
              <LoaderCircle className="size-4 animate-spin" />
              {t("common.loading")}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {items.length ? (
          <div className="divide-y divide-[#ece2d6]">
            {items.map((item) => (
              <button
                className={cn(
                  "relative w-full px-6 py-5 text-left transition hover:bg-[#fbf6ee]",
                  item.id === activeRequestId && "bg-[#f6f0e7]",
                )}
                key={item.id}
                type="button"
                onClick={() => onOpenRequest(item.id)}
              >
                {item.id === activeRequestId ? (
                  <span className="absolute inset-y-0 left-0 w-[3px] bg-[#2e2721]" />
                ) : null}

                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-[0.95rem] font-semibold text-ink/56">
                      {dateFormatter.format(new Date(item.createdAt))}
                    </p>
                    <StatusBadge status={item.status} />
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-[1.1rem] font-semibold leading-7 text-ink">{item.subject}</h3>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="border-[#ece2d6] bg-[#f5efe7] text-ink/52" variant="outline">
                        {t(getPqrCategoryTranslationKey(item.category))}
                      </Badge>
                      {item.attachmentCount ? (
                        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-ink/45">
                          <Paperclip className="size-3.5" />
                          {t("pqr.myRequests.attachmentCount", { count: item.attachmentCount })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="px-6 py-10">
            <p className="text-lg font-semibold text-ink">
              {searchQuery ? t("pqr.myRequests.searchEmptyTitle") : t("pqr.myRequests.emptyTitle")}
            </p>
            <p className="mt-2 text-sm leading-7 text-ink/58">
              {searchQuery ? t("pqr.myRequests.searchEmptyBody") : t("pqr.myRequests.emptyBody")}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-[#ece2d6] px-6 py-5 text-sm font-medium text-ink/58">
        <button
          className="inline-flex items-center gap-2 transition hover:text-ink disabled:cursor-not-allowed disabled:opacity-45"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          type="button"
        >
          <ChevronLeft className="size-4" />
          {t("pqr.actions.previousPage")}
        </button>
        <button
          className="inline-flex items-center gap-2 transition hover:text-ink disabled:cursor-not-allowed disabled:opacity-45"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          type="button"
        >
          {t("pqr.actions.nextPage")}
          <ChevronRight className="size-4" />
        </button>
      </div>
    </aside>
  );
}
